import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CampaignStatus, CampaignType } from '@dev/ee-api/ee-api-models';
import { push as Push } from 'redux-first-history';
import { useRouteMatch } from 'react-router-dom';
import useMobile from '@/services/hooks/useMobile';
import {
    SecondaryButton,
    SimpleLoader,
    SimplePopover,
    ToggleButtonGroup,
    Tooltip,
} from '@dev/ee-components';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { filter, map } from 'lodash-es';
import { Info } from '@material-ui/icons';
import {
    getCampaignsStore,
    SetAbSplitCurrentTemplate,
    SetAbSplitDisplayMode,
} from '@/store/campaigns/actions';
import { getConfigStore } from '@/store';
import { CampaignsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { getCampaignCreatorType } from '@/services/tools/getCampaignCreatorType';
import { AbSplitDisplayMode } from '@/store/campaigns/types';
import { StatusBadge } from '@/assets/StatusBadge';

import { TabNameEnum } from '../CampaignReportInterface';
import { isNewAbSplit } from '../../helperFunctions';
import { StyledProps } from '../../CampaignsInterface';


const AbSplitHeaderControlsWrapper = styled.div<StyledProps>`
    display: flex;
    align-items: ${(props): string => (props.isMobile ? 'flex-start' : 'center')};

    ${({ isMobile }): string =>
        isMobile &&
        `
    flex-direction: column-reverse;
    margin-top: 33px;

    & .popoverWrapper {
        margin-bottom: 13px;
        & button {
            padding: 0;
        }
    }
    & .MuiToggleButtonGroup-root {
        margin-bottom: 25px;
    }
    `}
`;

const HeaderControlsTooltipWrapper = styled.div`
    margin-left: 8px;
`;

const useViewEmailButtonClick = (campaignName: string): (() => void) =>  {
    const match = useRouteMatch();
    const { type } = match.params as {type: CampaignType};
    const config = useSelector(getConfigStore);
    const dispatch = useDispatch();

    return (): void => {
        if (config.isStandalone) {
            const campaignCreatorType = getCampaignCreatorType(type);
            dispatch(Push(
                `${CampaignsPaths.CampaignCreatorDetails.replace(pathsParams.type, campaignCreatorType).replace(
                    pathsParams.name,
                    safelyEncodeBase64Url(campaignName)
                )}`
            ));
        } else {
            window.location.hash = `${CampaignsPaths.OldViewCampaign.replace(
                ':name',
                safelyEncodeBase64Url(campaignName)
            )}`;
        }
    };

}

export const AdditionalHeader = (type: CampaignType): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const match = useRouteMatch();
    const isMobile = useMobile();
    const { pickedCampaign, abSplitHeaderControls } = useSelector(getCampaignsStore);
    const { tab, name } = match.params as any;

    const { campaign, templateLabels } = pickedCampaign;
    const isAbResultsTab = tab === TabNameEnum.AbSplit;
    let filteredTemplates;

    const campaignName = safelyDecodeBase64Url(name);
    const viewEmailButtonClick = useViewEmailButtonClick(campaignName);

    switch (type) {
        case CampaignType.Automated:
            
            const badge = pickedCampaign?.campaign ? <StatusBadge value={pickedCampaign.campaign.Status} status={pickedCampaign.campaign.Status} borderDisabled style={{ marginBottom: isMobile && '16px' }} /> : <SimpleLoader />;
            

            return (
                <>
                    {badge}
                    <SecondaryButton
                        text={t('viewemailcapital')}
                        onClick={viewEmailButtonClick}
                    />
                </>
            );
        case CampaignType.AbSplit:
            if (!campaign || !templateLabels) {
                return <SimpleLoader />;
            }
            filteredTemplates = isNewAbSplit(campaign?.CampaignTemplates)
                ? filter(campaign?.CampaignTemplates, (template) => !template.IsWinner)
                : campaign?.CampaignTemplates;

            return !isAbResultsTab ? (
                <AbSplitHeaderControlsWrapper isMobile={isMobile}>
                    {abSplitHeaderControls.displayMode === AbSplitDisplayMode.TestResult && (
                        <SimplePopover
                            value={abSplitHeaderControls.currentTemplate}
                            listItemWidth={'200px'}
                            type={'noborder'}
                            onItemClick={(event, pickedOption: { value: string; label: string }): void => {
                                dispatch(SetAbSplitCurrentTemplate(pickedOption.value));
                            }}
                            options={map(filteredTemplates, (template) => ({
                                value: template.Name,
                                label: `${t('version')} ${pickedCampaign.templateLabels[template.Name]}`,
                            }))}
                        />
                    )}
                    <ToggleButtonGroup
                        id={'absplit-switch-templates'}
                        onChange={(value: AbSplitDisplayMode): void => {
                            dispatch(SetAbSplitDisplayMode(value));
                        }}
                        options={[
                            {
                                label: t('testresult'),
                                value: AbSplitDisplayMode.TestResult,
                            },
                            {
                                label: `${t('winner')}!`,
                                value: AbSplitDisplayMode.Winner,
                                disabled: campaign.Status !== CampaignStatus.Completed,
                            },
                        ]}
                        value={abSplitHeaderControls.displayMode}
                    />
                    {!isMobile && (
                        <HeaderControlsTooltipWrapper>
                            <Tooltip
                                id={'info-absplit-switch-templates'}
                                title={t('absplitheadercontrolstooltip')}
                                color={'#fff'}
                                interactive
                                placement={'bottom-end'}
                                PopperProps={{ keepMounted: false, disablePortal: true }}
                            >
                                <Info fontSize="small" color={'disabled'} />
                            </Tooltip>
                        </HeaderControlsTooltipWrapper>
                    )}
                </AbSplitHeaderControlsWrapper>
            ) : (
                <SecondaryButton
                    text={t('viewemailcapital')}
                    onClick={viewEmailButtonClick}
                />
            );
        default:
            return campaign?.FromAutomation ? (
                <div />
            ) : (
                <SecondaryButton
                    text={t('viewemailcapital')}
                    onClick={viewEmailButtonClick}
                />
            );
    }
}